import { useState, useMemo } from 'react';
import {
  X,
  Building2,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader,
  Sparkles,
  Lock,
  ExternalLink,
} from 'lucide-react';
import api from '../services/api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';

const CreateOrganizationModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1); // 1: Form, 2: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tier: 'founder', // Default to Founder (most popular)
  });

  // Logo upload
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Created organization
  const [createdOrg, setCreatedOrg] = useState(null);

  // Generate slug from name
  const slug = useMemo(() => {
    return formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 30);
  }, [formData.name]);

  // Generate initials for default logo
  const initials = useMemo(() => {
    return formData.name
      .split(' ')
      .slice(0, 2)
      .map(word => word[0])
      .join('')
      .toUpperCase() || 'ORG';
  }, [formData.name]);

  // Auto-suggest name from user email domain
  const suggestedName = useMemo(() => {
    if (!user?.email) return '';
    const domain = user.email.split('@')[1];
    if (!domain || domain.includes('gmail') || domain.includes('yahoo') || domain.includes('hotmail')) {
      return '';
    }
    return domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1);
  }, [user?.email]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle logo upload
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, logo: 'Please upload an image file' }));
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: 'Logo must be less than 2MB' }));
      return;
    }

    setLogoFile(file);
    setErrors((prev) => ({ ...prev, logo: '' }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      if (formData.description) {
        submitData.append('description', formData.description.trim());
      }
      submitData.append('subscription.tier', formData.tier);
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      // Create organization
      const response = await api.post('/organizations', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setCreatedOrg(response.data);
      setStep(2);

      logger.info('Organization created successfully', {
        orgId: response.data._id,
        name: response.data.name
      });

      // Call onSuccess callback after 2 seconds
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(response.data);
        }
      }, 2000);
    } catch (err) {
      logger.apiError('/organizations', err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Failed to create organization. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (loading) return;

    // Reset form
    setStep(1);
    setFormData({ name: '', description: '', tier: 'founder' });
    setLogoFile(null);
    setLogoPreview(null);
    setErrors({});
    setError('');
    setCreatedOrg(null);

    onClose();
  };

  // Don't render if not open
  if (!isOpen) return null;

  // Tier options - unified with unified pricing model (Demo, Founder, Pro, Enterprise)
  const tiers = [
    {
      id: 'demo',
      name: 'Demo',
      tagline: 'Evaluate the system',
      price: '$0',
    },
    {
      id: 'founder',
      name: 'Founder',
      tagline: 'Growing operators and early-stage teams',
      price: '$149/mo',
      popular: true,
    },
    {
      id: 'pro',
      name: 'Pro',
      tagline: 'Multi-warehouse scale',
      price: '$399/mo',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      tagline: 'Custom deployments',
      price: 'Custom',
    },
  ];

  return (
    <div className="create-org-modal-overlay">
      <div className="create-org-modal">
        {/* Close button */}
        {!loading && (
          <button
            onClick={handleClose}
            className="create-org-modal-close"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Step 1: Form */}
        {step === 1 && (
          <>
            {/* Header - Clarified purpose */}
            <div className="create-org-modal-header">
              <div className="create-org-modal-header-icon">
                <Building2 className="w-6 h-6" />
              </div>
              <h2 className="create-org-modal-title">Create Your Workspace</h2>
              <p className="create-org-modal-subtitle">
                This is your company workspace. Products, warehouses, users, billing, and integrations live here.
              </p>
            </div>

            {/* Error banner */}
            {error && (
              <div className="create-org-error-banner">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {/* Two-column form layout */}
            <form onSubmit={handleSubmit} className="create-org-form">
              <div className="create-org-two-column">
                {/* Left Column - Primary (Organization Details) */}
                <div className="create-org-primary">
                  {/* Organization Name */}
                  <div className="create-org-form-group">
                    <label htmlFor="name" className="create-org-label">
                      Workspace Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={suggestedName || "Acme Corporation"}
                      className={`create-org-input ${
                        errors.name ? 'create-org-input-error' : ''
                      }`}
                      disabled={loading}
                      maxLength={100}
                      autoFocus
                    />
                    <p className="create-org-helper-text">
                      This name appears on invoices, reports, and team invites.
                    </p>
                    {slug && formData.name.length >= 3 && (
                      <p className="create-org-slug-preview">
                        Workspace URL: <span className="font-medium">gabhan.io/org/{slug}</span>
                      </p>
                    )}
                    {errors.name && (
                      <p className="create-org-error-text">{errors.name}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="create-org-form-group">
                    <label htmlFor="description" className="create-org-label">
                      Description <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="What does your business do?"
                      rows={2}
                      className={`create-org-textarea ${
                        errors.description ? 'create-org-input-error' : ''
                      }`}
                      disabled={loading}
                      maxLength={500}
                    />
                    {errors.description && (
                      <p className="create-org-error-text">{errors.description}</p>
                    )}
                  </div>

                  {/* Logo Upload - Made optional feeling */}
                  <div className="create-org-form-group">
                    <label className="create-org-label">
                      Logo <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <div className="create-org-logo-upload">
                      {logoPreview ? (
                        <div className="create-org-logo-preview">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="create-org-logo-img"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setLogoFile(null);
                              setLogoPreview(null);
                            }}
                            className="create-org-logo-remove"
                            disabled={loading}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="create-org-logo-default-wrapper">
                          {/* Default initials logo */}
                          <div className="create-org-logo-default">
                            <span>{initials}</span>
                          </div>
                          <label className="create-org-logo-upload-btn">
                            <Upload className="w-4 h-4" />
                            <span>Upload</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleLogoChange}
                              className="create-org-logo-input"
                              disabled={loading}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    <p className="create-org-helper-text">
                      You can skip this for now. Upload later in Settings.
                    </p>
                    {errors.logo && (
                      <p className="create-org-error-text">{errors.logo}</p>
                    )}
                  </div>
                </div>

                {/* Right Column - Secondary (Plan Selection) */}
                <div className="create-org-secondary">
                  <div className="create-org-plan-section">
                    <label className="create-org-label">
                      Choose a plan
                    </label>
                    <p className="create-org-plan-subtext">
                      You can change this anytime
                    </p>

                    <div className="create-org-plan-list">
                      {tiers.map((tier) => (
                        <label
                          key={tier.id}
                          className={`create-org-plan-item ${
                            formData.tier === tier.id
                              ? 'create-org-plan-item-selected'
                              : ''
                          }`}
                        >
                          <input
                            type="radio"
                            name="tier"
                            value={tier.id}
                            checked={formData.tier === tier.id}
                            onChange={handleChange}
                            className="create-org-plan-radio"
                            disabled={loading}
                          />
                          <div className="create-org-plan-content">
                            <div className="create-org-plan-header">
                              <span className="create-org-plan-name">{tier.name}</span>
                              {tier.popular && (
                                <span className="create-org-plan-badge">
                                  <Sparkles className="w-3 h-3" />
                                  Most Popular
                                </span>
                              )}
                            </div>
                            <span className="create-org-plan-tagline">{tier.tagline}</span>
                          </div>
                          <span className="create-org-plan-price">{tier.price}</span>
                        </label>
                      ))}
                    </div>

                    <a
                      href="https://www.gabhan.io/pricing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="create-org-compare-link"
                    >
                      Compare plans <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Actions - Full width at bottom */}
              <div className="create-org-actions">
                <button
                  type="button"
                  onClick={handleClose}
                  className="create-org-btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <div className="create-org-submit-wrapper">
                  <button
                    type="submit"
                    className="create-org-btn-primary"
                    disabled={loading || !formData.name.trim()}
                  >
                    {loading ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Building2 className="w-4 h-4" />
                        Create Workspace
                      </>
                    )}
                  </button>
                  <span className="create-org-submit-hint">Takes less than 30 seconds</span>
                </div>
              </div>

              {/* Trust signal */}
              <div className="create-org-trust">
                <Lock className="w-3.5 h-3.5" />
                <span>You can change plans, name, and settings at any time.</span>
              </div>
            </form>
          </>
        )}

        {/* Step 2: Success */}
        {step === 2 && createdOrg && (
          <div className="create-org-success">
            <div className="create-org-success-icon">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
            <h2 className="create-org-success-title">
              Organization Created Successfully!
            </h2>
            <p className="create-org-success-subtitle">
              Welcome to <strong>{createdOrg.name}</strong>. Switching to your
              new organization...
            </p>
            <div className="create-org-success-loader">
              <Loader className="w-6 h-6 animate-spin text-brand-600" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateOrganizationModal;
